import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import SimpleFooter from 'src/components/layouts/SimpleFooter/SimpleFooter';
import TopBar from 'src/components/ui/TopBar/TopBar';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { ControlledInput } from 'src/components/ui/Input/Input';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Icon from 'src/components/ui/Icon/Icon';
import TrackingErrorModal from 'src/components/modals/TrackingErrorModal/TrackingErrorModal';
import styles from './styles/TrackingAuthForm.module.scss';
import { SaleOrderTrackingAuthenticationIn, useCustomerTrackingKeyCreate } from '../services/customerTracking';

function TrackingOrderNumber() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mutation = useCustomerTrackingKeyCreate();
  const { orderNumber } = useParams();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorDetail, setErrorDetail] = useState('');

  const postalCodeSchema = z.object({
    postalCode: z
      .string()
      .min(4, `${t('tracking.required_postal_code')}`)
      .max(10, `${t('tracking.max_postal_code')}`),
  });

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(postalCodeSchema),
    mode: 'onSubmit',
    defaultValues: {
      postalCode: '',
    },
  });

  useEffect(() => {
    const cachedValue = localStorage.getItem('postalCode');
    if (cachedValue) {
      setValue('postalCode', cachedValue);
    }
  }, [setValue]);

  const onSubmit = async (data: { postalCode: string }) => {
    try {
      localStorage.setItem('postalCode', data.postalCode);
      const customerData: SaleOrderTrackingAuthenticationIn = {
        key: null,
        order_number: orderNumber,
        zip_code: data.postalCode,
      };
      const response = await mutation.mutateAsync({
        body: customerData,
      });

      const trackingKey = response?.key || '';
      navigate(`/tracking/details/${trackingKey}`);
    } catch (error: any) {
      const errorDetailMessage = (error?.detail ?? undefined) as string | undefined;
      if (errorDetailMessage?.includes('expired on')) {
        setErrorDetail(t('tracking.error_expired'));
      } else if (errorDetailMessage?.includes('not found')) {
        setErrorDetail(t('tracking.not_found_description'));
      } else {
        setErrorDetail(t('tracking.error_500'));
      }
      setIsErrorModalOpen(true);
    }
  };

  return (
    <div className={classNames(styles.root)}>
      <div className={styles.topbar}>
        <TopBar />
      </div>
      <div className={styles.main}>
        <div className="w-full h-16">
          <button onClick={() => navigate('/tracking')} type="button" className="flex gap-x-8">
            <Icon className="text-full opacity-40 rotate-180" name="arrowRight" width={20} height={20} />
            <span className="text-faded typo-text-14--medium">{t('general.go_back')}</span>
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col md:items-center justify-center w-full gap-60">
          <div>
            <h1 className="typo-title-36 mb-12 text-center">{t('tracking.enter_postcode')}</h1>
            <p className="typo-text-16 text-faded text-center">{t('tracking.enter_postcode_description')}</p>
          </div>
          <ControlledInput
            control={control}
            {...register('postalCode')}
            placeholder="1234"
            className={styles.input}
            error={!!errors.postalCode}
            errorMessage={errors.postalCode?.message}
            autoComplete="postal-code"
          />
          <Button
            title={t('tracking.submit_postcode')}
            tone={ButtonToneProps.PRIMARY_BLUE}
            className="w-full max-w-460 h-full min-h-20"
            type="submit"
          />
        </form>
        <div className="h-16" />
      </div>
      <SimpleFooter />

      <TrackingErrorModal
        isOpen={isErrorModalOpen}
        onClose={() => {
          setIsErrorModalOpen(false);
          navigate('/tracking');
        }}
        errorDetail={errorDetail}
      />
    </div>
  );
}

export default TrackingOrderNumber;
